import type { Order } from "@nwt/norce-pwa/dist/runtime/types/api/checkout/orders/orders";
// The app's own Locale type, not norce-pwa's: LOCALES is app-level config and
// only this one carries `paymentAdapters` and the object form of
// `checkoutChannels` (norce-pwa's copy still has channels as plain strings).
import type { CheckoutChannelEntry, Locale } from "~/types/locale";

type CheckoutCustomerType = "person" | "organization";

export const useCheckoutChannel = () => {
	const config = useRuntimeConfig();
	const { locale } = useI18n();
	const norceStore = useNorceStore();
	const { norceOrders } = useNorcePwa();

	const locales = (config.public.norce.locales ?? []) as Locale[];
	const localeConfig = computed(() =>
		locales.find(
			(entry) => entry.code === locale.value || entry.iso === locale.value
		)
	);

	const channelOf = (entry?: CheckoutChannelEntry) =>
		typeof entry === "string" ? entry : entry?.channel;

	/** The object form of a customer type's entry, or undefined for the
	 * channel-only string form. */
	const configOf = (type: CheckoutCustomerType) => {
		const entry = localeConfig.value?.checkoutChannels?.[type];
		return typeof entry === "string" ? undefined : entry;
	};

	const getCheckoutChannelForType = (type: CheckoutCustomerType) => {
		const { checkoutChannels, channel } = localeConfig.value ?? {};
		if (!checkoutChannels?.person && !checkoutChannels?.organization) {
			return channel;
		}
		return channelOf(checkoutChannels[type]) ?? channel;
	};

	/**
	 * The payment adapters offered for a customer type: its own list first (the
	 * providers enabled on its NCO channel), then the locale-wide list. Empty
	 * means no list is configured — checkout then mounts the single
	 * `paymentAdapter` / `PUBLIC_NORCE_PAYMENT_ADAPTER`.
	 */
	const getPaymentAdaptersForType = (type: CheckoutCustomerType) => {
		const perType = configOf(type)?.paymentAdapters?.filter(Boolean);
		if (perType?.length) return perType;
		return localeConfig.value?.paymentAdapters?.filter(Boolean) ?? [];
	};

	/**
	 * Which adapter to preselect for a customer type: its explicit
	 * `defaultPaymentAdapter`, else the locale's `paymentAdapter`, else the first
	 * entry — each only if actually offered for that type.
	 */
	const getDefaultPaymentAdapterForType = (type: CheckoutCustomerType) => {
		const list = getPaymentAdaptersForType(type);
		const explicit = configOf(type)?.defaultPaymentAdapter;
		if (explicit && list.includes(explicit)) return explicit;
		const localeDefault =
			localeConfig.value?.paymentAdapter ?? config.public.norce.paymentAdapter;
		if (localeDefault && list.includes(localeDefault)) return localeDefault;
		return list[0] ?? "";
	};

	/**
	 * Which customer type a checkout channel belongs to, or undefined when the
	 * channel cannot identify one. Lets checkout start on the right type after a
	 * reload — the order's `customer.type` is authoritative but only known after
	 * a round trip, and mounting the assumed type's payment adapter in the
	 * meantime creates a payment on the wrong channel.
	 *
	 * Only an unambiguous match counts: when both types share a channel (or the
	 * locale configures none) the channel says nothing about the type.
	 */
	const getCustomerTypeForChannel = (
		channel?: string
	): CheckoutCustomerType | undefined => {
		if (!channel) return undefined;
		const person = getCheckoutChannelForType("person");
		const organization = getCheckoutChannelForType("organization");
		if (person === organization) return undefined;
		if (channel === organization) return "organization";
		if (channel === person) return "person";
		return undefined;
	};

	const getCheckoutChannelCandidates = (preferred?: string) => {
		return Array.from(
			new Set(
				[
					preferred,
					// The channel in the store is the one the current order was created
					// on — try it before falling back to probing the other types, so a
					// switched channel isn't silently reverted by a lucky hit.
					norceStore.checkoutChannel,
					getCheckoutChannelForType("person"),
					getCheckoutChannelForType("organization"),
					localeConfig.value?.channel
				].filter((channel): channel is string => Boolean(channel))
			)
		);
	};

	const getCheckoutQueryParams = (preferred?: string) => ({
		localeCode: locale.value,
		...(preferred || norceStore.checkoutChannel
			? { checkoutChannel: preferred || norceStore.checkoutChannel }
			: {})
	});

	const setCheckoutChannelForType = (type: CheckoutCustomerType) => {
		const channel = getCheckoutChannelForType(type);
		norceStore.setCheckoutChannel(channel);
		return channel;
	};

	const ensureCheckoutChannel = (type: CheckoutCustomerType = "person") => {
		norceStore.setCheckoutChannel(getCheckoutChannelForType(type));
		return norceStore.checkoutChannel;
	};

	const resolveOrderByChannel = async (orderId: string) => {
		const candidates = getCheckoutChannelCandidates();
		let lastError: unknown;

		for (const channel of candidates) {
			try {
				const order = (await norceOrders.getOrderById({
					orderId,
					...getCheckoutQueryParams(channel)
				})) as Order;
				norceStore.setCheckoutChannel(channel);
				return order;
			} catch (error) {
				lastError = error;
			}
		}

		if (!candidates.length) {
			return norceOrders.getOrderById({
				orderId,
				...getCheckoutQueryParams()
			});
		}

		throw lastError;
	};

	return {
		ensureCheckoutChannel,
		getCheckoutChannelCandidates,
		getCheckoutChannelForType,
		getCheckoutQueryParams,
		getCustomerTypeForChannel,
		getDefaultPaymentAdapterForType,
		getPaymentAdaptersForType,
		resolveOrderByChannel,
		setCheckoutChannelForType
	};
};
